import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { EMPTY_FILTER_ID } from "constants/app.constants";
import { EvaChart } from "constants/charts.enums";
import { useChartsContext } from "contexts";
import { resolveWellStatusSelection } from "entities/charts/eva/requests/wellStatus";
import { DEFAULT_WELL_STATUS_CURRENT_REQUEST } from "entities/charts/eva/requests/wellStatusCurrent";
import { defaultWellStatusOverTimeRequest } from "entities/charts/eva/requests/wellStatusOverTime";
import { createEntityState } from "entities/charts/factory";
import { updateChart } from "store/features/charts/chartSlice";
import { RootState } from "store/rootReducer";
import {
  ChartRequestType,
  WellStatusChartRequestType,
  WellStatusCurrentChartRequestType,
  WellStatusFocus,
  WellStatusOverTimeChartRequestType
} from "types";

import { usePostWellStatusData } from "api/charts/usePostWellStatusData";

import { useChartEntities } from "./useChartEntities";
import {
  hasPickedChartFocusPalette,
  useWellStatusLegendColors
} from "./useWellStatusLegendOrder";

// All selectable fields across BOTH Well Status charts, all optional — the entity
// request may hold either shape (or a stale one mid chart-switch), so read
// leniently and rebuild cleanly per the ACTIVE chartType below. Mirrors the
// rig/frac controller's RigFracSelections.
type WellStatusSelections = Partial<WellStatusOverTimeChartRequestType> &
  Partial<WellStatusCurrentChartRequestType>;

/**
 * Data controller for BOTH Well Status charts: Status Over Time (stacked
 * bars per time bucket) and Current Well Status (ranked horizontal bars). Builds
 * the lean request per the active chartType and POSTs via the shared endpoint.
 *
 * Deliberately NOT a fork of useChartDataController — these charts carry none of
 * the wells production payload (no products, forecast, normalization, decline).
 * But unlike the rig/frac controller they ARE wells charts, so they do carry the
 * dashboard's active filter and the focus grouping. There is no map bbox: the
 * charts follow the filter only, never the viewport (research D3).
 */
export function useWellStatusController() {
  const { id } = useChartsContext();
  const dispatch = useDispatch();
  const { chartType, request: entityRequest, chartFocus } = useChartEntities(id);

  const isCurrent = chartType === EvaChart.WellStatusCurrent;
  const isWellStatus = chartType === EvaChart.WellStatusOverTime || isCurrent;

  // The entity request field is typed as the wells ChartRequestType; these charts
  // store their own lean request there (same cast pattern as the rig/frac controller).
  const selections = entityRequest as unknown as Maybe<WellStatusSelections>;

  // Well filter — the ONLY well scoping. Wells charts read state.filter.
  //
  // The EMPTY_FILTER_ID sentinel means "no filter applied", not "a filter whose id
  // is that string". Normalized to null so the request carries no filterId at all —
  // the same distinction the wells controller draws before it requests.
  //
  // Note what this does NOT buy: upstream treats both spellings identically.
  // GroupBySvc short-circuits an empty OR sentinel filter id to an empty response
  // before any lookup, and this widget reaches wells only through GroupBy, so
  // either way it resolves to zero groups. With no dashboard filter applied these
  // charts render "No Data Available" by construction, NOT org-wide counts.
  const rawFilterId = useSelector((state: RootState) => state.filter.filterId?.id);
  const filterId = !rawFilterId || rawFilterId === EMPTY_FILTER_ID ? null : rawFilterId;

  // Focus: the global well focus, unless the chart's own Chart Focus override is
  // active (the existing features.chartFocus feature — research D9). Both modes
  // supply a group-by property + bin; colours differ per mode, see below.
  const globalGroupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  const isChartFocusActive = chartFocus?.active ?? false;
  const chartFocusGroupBy = chartFocus?.properties?.groupBy;
  const chartFocusPalette = chartFocus?.properties?.colorPalette;
  // The palette the MAP and the focus legend are painted with. Used whenever the
  // chart is following the global focus — see the palette resolution below.
  const globalPalette = useSelector(
    (state: RootState) => state.userSetting.activeColorPalette
  );

  const isPickedChartFocusPalette = hasPickedChartFocusPalette(
    chartFocus,
    globalPalette?.id
  );

  const activeGroupBy = isChartFocusActive ? chartFocusGroupBy : globalGroupBy;
  const focusProperty = activeGroupBy?.property;
  // Max Bins, Max Bin Criteria and Min count all live on this bin object, and the
  // Chart Focus panel offers Max Bins precisely when the property is NOT bin-able
  // (a text property such as Operator), so the bin has to go out for every
  // property type — the same way the wells chart's request builder sends it.
  //
  // Sent RAW, never through sanitizeBinValues: the server's converters read an
  // empty string as null, while the sanitizer would turn it into a 0 that the
  // request validator rejects. Values are emptied rather than removed for the same
  // reason.
  const rawFocusBin = activeGroupBy?.bin;
  const maxBins = Number(rawFocusBin?.MaxBins);
  const minSize = Number(rawFocusBin?.MinSize);
  const sanitizedFocusBin = rawFocusBin
    ? {
        ...rawFocusBin,
        // Max Bins and Min count are free text typed on the text property itself,
        // and `min`/`type=number` stops only the spinner, not typing or a paste.
        //
        // Max Bins of 0 or less 400s the WHOLE widget (the request validator), and
        // chart-svc reads the field as an int, so a decimal binds to null and the
        // cap silently does nothing. Keep it only when it is a positive integer.
        MaxBins:
          Number.isInteger(maxBins) && maxBins > 0 ? rawFocusBin.MaxBins : ("" as const),
        // A Min count of 0 asks for nothing, but data-svc leaves its fast text
        // group-by whenever min_size is set at all, so it buys the slow aggregation
        // for identical output.
        MinSize: minSize > 0 ? rawFocusBin.MinSize : ("" as const),
        // Bin size, Less than and Greater than are numeric bounds carried forward
        // from a previously selected numeric property and mean nothing on a text
        // group-by. The panel hides their inputs once the focus is text, so the user
        // cannot clear a stale one — and a Bin size of 0 also 400s the widget.
        ...(activeGroupBy?.canBin
          ? {}
          : { BinSize: "" as const, LessThan: "" as const, GreaterThan: "" as const })
      }
    : rawFocusBin;
  // A bin saved with HideOthers: true must not reach chart-svc, which still honours it.
  const focusBin = sanitizedFocusBin && { ...sanitizedFocusBin, HideOthers: false };
  // The palette that colours the series. Chart Focus supplies its own; otherwise
  // the chart must use the GLOBAL active palette, not null — null makes chart-svc
  // fall back to its stock categorical palette, which is why the bars did not
  // match the focus legend / map colours (product: "Colour palette
  // should match the focus").
  const activePalette = isChartFocusActive ? chartFocusPalette : globalPalette;
  // Bin -> hex from the legend the user is looking at; see the focus block below.
  const legendColors = useWellStatusLegendColors();

  // Validated against the ACTIVE chart's vocabulary, not just defaulted when
  // absent: the entity request survives a chart-type switch, and on Azure the two
  // charts' status vocabularies are disjoint. See resolveWellStatusSelection.
  const status = resolveWellStatusSelection(selections?.status, isCurrent);
  // "Show every status" is an explicit flag on the request, never a magic
  // `status` value — the selector translates its dropdown entry into this.
  const allStatuses = isCurrent && selections?.allStatuses === true;
  const timeScale = selections?.timeScale ?? defaultWellStatusOverTimeRequest().timeScale;
  const customRangeFrom = selections?.customRange?.from;
  const customRangeTo = selections?.customRange?.to;
  // Over-time-only field: the flag is pinned on the current-status chart (its
  // Rust path drops legs when it's false), so it is NEVER read off the
  // persisted request there — `chart.request` survives a chart-type switch,
  // and a `false` written by the over-time toggle would otherwise leak onto
  // the current-status chart via this shared entity field.
  const includeDrainLegs = isCurrent
    ? DEFAULT_WELL_STATUS_CURRENT_REQUEST.includeDrainLegs
    : selections?.includeDrainLegs ?? defaultWellStatusOverTimeRequest().includeDrainLegs;

  const request = useMemo(() => {
    if (!isWellStatus) return null;
    // No focus property yet (focus still loading) — don't POST a request the
    // server would reject; the wrapper shows its loading/empty state instead.
    if (!focusProperty) return null;

    // Colour comes from two things, and the second is what makes the bars match
    // the focus rather than merely use the same palette:
    //
    //  * colorPalette — the ACTIVE palette, spread server-side over the bins it
    //    returns. On its own this only agrees with the legend when the server's
    //    bin order (count-descending) happens to match the focus's, which is why
    //    the bars could come back the wrong colours;
    //  * seriesColors — the legend's own bin→hex map, which pins each bin to the
    //    colour the user is looking at on the map regardless of either order.
    //
    // The legend rides in with the map response, so it can be missing on the very
    // first request; the field is omitted then and the server falls back to
    // spreading the palette, exactly as before.
    const focus: WellStatusFocus = {
      property: focusProperty,
      // Display name for the export's first column header; the on-screen table
      // already shows it (via focusTitle), and chart-svc cannot derive it.
      title: activeGroupBy?.title,
      bin: focusBin ?? null,
      // The chartFocus palette is the narrower ColorPaletteBase; the request field
      // takes the wider ColorPaletteSetting and the backend only reads the fields
      // they share (id/colors/colorScale/paletteType/reverse), so cast at this seam
      // — the same cast the rig/frac controller makes for its palette.
      colorPalette: (activePalette ?? null) as unknown as Nullable<
        WellStatusFocus["colorPalette"]
      >,
      // The server lets seriesColors WIN over the palette, and this map is painted
      // by the GLOBAL focus — so while it is sent, a palette picked for this chart
      // is spread over nothing and the bars keep the map's colours. Omit it then
      // and let the picked palette colour the bins.
      ...(!isPickedChartFocusPalette && Object.keys(legendColors).length > 0
        ? { seriesColors: legendColors }
        : {})
    };

    if (isCurrent) {
      const currentReq = createEntityState(EvaChart.WellStatusCurrent, {
        status: allStatuses ? "" : status,
        allStatuses,
        filterId,
        includeDrainLegs
      } as Partial<WellStatusCurrentChartRequestType>) as WellStatusCurrentChartRequestType;
      // Assigned AFTER createEntityState, never as an override: the
      // merge-with-defaults deep-merges, so a narrower focus passed as an
      // override would keep the previous object's leftover keys.
      currentReq.focus = focus;
      return currentReq;
    }

    const overTimeReq = createEntityState(EvaChart.WellStatusOverTime, {
      status,
      timeScale,
      customRange:
        customRangeFrom && customRangeTo
          ? { from: customRangeFrom, to: customRangeTo }
          : null,
      filterId,
      includeDrainLegs
    } as Partial<WellStatusOverTimeChartRequestType>) as WellStatusOverTimeChartRequestType;
    overTimeReq.focus = focus;
    return overTimeReq;
    // Scalar deps only: the updateChart(request) echo below replaces the entity
    // request object, and object deps would rebuild the memo → re-POST → loop.
    // The palette is depended on via id AND reverse — the picker's reverse control
    // returns the SAME palette with only `reverse` flipped, so id alone would
    // silently drop the re-POST and the bars would never re-colour.
  }, [
    isWellStatus,
    isCurrent,
    status,
    allStatuses,
    timeScale,
    customRangeFrom,
    customRangeTo,
    filterId,
    includeDrainLegs,
    focusProperty,
    activeGroupBy?.title,
    // NOT legendColors, deliberately. The chart now paints its bars from the
    // legend client-side (useWellStatusSeriesColorMap), so recolouring or
    // re-ordering the focus bins needs no new data — and re-POSTing for it cost a
    // full aggregation (3.5-6.9s on a large filter) to return counts identical to
    // the ones already on screen. The map still RIDES ALONG on the next genuine
    // request (this memo reads it from scope), so the server's stamped colours
    // stay fresh for the export and for the first paint.
    focusBin?.BinType,
    focusBin?.BinSize,
    focusBin?.LessThan,
    focusBin?.GreaterThan,
    focusBin?.MinSize,
    // NOT UseDynamicBins: the whole bin object is forwarded as JSON, but there is
    // no such field on WellStatusBin or data::Bin, so chart-svc drops it and no
    // downstream binning can change. Depending on it would only re-POST for an
    // identical payload. Add it back if the proto ever carries it.
    // Honoured downstream too — chart-svc validates them and data-svc-rs passes
    // them to GroupBy — so without them here, changing Max Bins, its sort order
    // or the quantile type/count leaves the memo stale and the chart silently
    // keeps its previous binning. (Note the casing: the bin's own fields are
    // PascalCase, but QuantileState's are camelCase.)
    focusBin?.MaxBins,
    focusBin?.MaxBinsSortOrder,
    focusBin?.MaxBinsSortField,
    focusBin?.Quantile?.quantileType,
    focusBin?.Quantile?.numQuantiles,
    isChartFocusActive,
    isPickedChartFocusPalette,
    activePalette?.id,
    activePalette?.reverse
  ]);

  const { data: apiResponse, loading, error } = usePostWellStatusData(request);

  // Persist request + response on the chart entity so the render/legend/screenshot
  // paths pick them up.
  //
  // Deps are INTENTIONALLY [apiResponse] only. This echo must fire once, when a
  // response lands, persisting that response together with the request that
  // produced it. The abort-on-change in usePostWellStatusData guarantees a new
  // apiResponse always corresponds to the latest request, so the current request
  // closure is the correct pairing. Adding `request` (a NEW object on every memo
  // rebuild) would fire this on each selection change with the PREVIOUS
  // apiResponse, briefly persisting a mismatched pair.
  useEffect(() => {
    if (!apiResponse || !request) return;
    dispatch(
      updateChart({
        id,
        chartType,
        request: request as unknown as ChartRequestType,
        response: apiResponse
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResponse]);

  return {
    loading,
    error,
    apiResponse,
    request: request as Nullable<WellStatusChartRequestType>
  };
}
